/**
 * Die Dokumentbeschreibung — reine Daten, KEIN Renderer.
 *
 * ═══ WARUM DIESE ZWISCHENSCHICHT ══════════════════════════════════════════
 * Die Altgeneratoren des Repos mischen Wortlaut und Zeichnung: sie
 * entscheiden mitten in einem `doc.text(...)`, was fachlich gilt. Das laesst
 * sich nicht pruefen, ohne ein PDF zu erzeugen, und es hat schon einmal dazu
 * gefuehrt, dass ein Generator den Titel eines gesperrten Dokuments druckte,
 * weil er die Fachlogik nachgebaut hat statt sie zu fragen.
 *
 * Deshalb hier: `baueDokument()` entscheidet, `renderBauordnungPdf()`
 * zeichnet. Geprueft wird die BESCHREIBUNG, nie die Bytes — ein Test gegen
 * PDF-Bytes braeche bei jeder Millimeteraenderung.
 *
 * Diese Datei darf nichts aus `jspdf` und nichts aus `docx` importieren.
 */

export type KnotenArt =
    | 'ueberschrift'
    | 'absatz'
    | 'liste'
    | 'regeln'
    | 'tabelle'
    | 'hinweis'
    | 'leerbefund'
    | 'luecke'
    | 'unterschriftsfeld'
    | 'entwurfsvermerk'
    | 'seitenumbruch';

export interface KnotenUeberschrift {
    art: 'ueberschrift';
    ebene: 1 | 2 | 3;
    /** Gliederungsziffer, z. B. „7", „C" oder „Anhang 2". Leer bei Rahmenkapiteln. */
    nummer?: string;
    text: string;
    /** Kapitelkennung — nur fuer das Inhaltsverzeichnis und Sprungmarken. */
    kapitel?: string;
}

export interface KnotenAbsatz { art: 'absatz'; text: string; betont?: boolean }
export interface KnotenListe { art: 'liste'; punkte: string[] }

/**
 * Eine erfasste Regelung. Verantwortlich und Rhythmus gehören zur Regel und
 * stehen deshalb an ihr, nicht in einer zweiten Tabelle darunter: die frühere
 * Tabelle „Regelung | Verantwortlich | Rhythmus" musste den Wortlaut kürzen
 * und schnitt ihn mitten im Wort ab.
 */
export interface Regelpunkt {
    /**
     * „7.1", „7.2": vergeben von `baueDokument`, nie vom Renderer gezählt.
     * Fehlt sie (Kapitel ohne Gliederungsziffer), setzt der Renderer einen
     * Aufzählungspunkt.
     */
    nummer?: string;
    text: string;
    verantwortlich?: string;
    rhythmus?: string;
}
export interface KnotenRegeln { art: 'regeln'; punkte: Regelpunkt[] }

/** Satzangabe einer Tabellenspalte: feste Breite in mm oder Anteil am Rest. */
export interface SpaltenMass { breite?: number; gewicht?: number }
export interface KnotenTabelle {
    art: 'tabelle';
    kopf: string[];
    zeilen: string[][];
    /** Je Spalte; ohne Angabe teilen sich alle Spalten die Breite gleich. */
    spalten?: SpaltenMass[];
}
export interface KnotenHinweis { art: 'hinweis'; text: string }
/** „Hier steht noch nichts" — ausdruecklich, nie Schweigen. */
export interface KnotenLeerbefund { art: 'leerbefund'; text: string }
export interface KnotenLuecke { art: 'luecke'; nr: number; text: string }
export interface KnotenUnterschrift {
    art: 'unterschriftsfeld';
    /** Der ERLASSENDE. Ein Feld fuer den Verfasser gibt es nicht. */
    zeilen: { rolle: string; name: string; firma: string }[];
}
export interface KnotenEntwurf { art: 'entwurfsvermerk'; text: string }
export interface KnotenUmbruch { art: 'seitenumbruch' }

export type Knoten =
    | KnotenUeberschrift | KnotenAbsatz | KnotenListe | KnotenRegeln | KnotenTabelle
    | KnotenHinweis | KnotenLeerbefund | KnotenLuecke | KnotenUnterschrift
    | KnotenEntwurf | KnotenUmbruch;

/**
 * Wer das Dokument erlässt. Steht oben auf der Titelseite; das verfassende
 * Büro erscheint nur in `verfasserZeile` (Spezifikation A, „Wer erlässt, wer
 * verfasst": nur der Erlassende hat das Hausrecht).
 */
export interface Herausgeber {
    /** Firma des Erlassenden. Leer, solange sie nicht erfasst ist. */
    firma: string;
    /** „Bauherr", „Generalunternehmer", „Bauleitung" oder leer. */
    rolle: string;
}

export interface Dokument {
    /** Titel auf der Titelseite und in der Kopfzeile. */
    titel: string;
    untertitel: string;
    herausgeber: Herausgeber;
    /** Bauvorhaben für die Kopfzeile ab Seite 2, z. B. „Neubau Labor (Projekt-Nr. 6301)". */
    bauvorhaben: string;
    /** Stand als TT.MM.JJJJ für die Fußzeile; leer, wenn kein Stand gesetzt ist. */
    standDatum: string;
    /** Angabenliste der Titelseite: Merkmal → Angabe. Die erste Zeile ist die Hauptsache. */
    deckblatt: { merkmal: string; angabe: string }[];
    /** Absätze unter „Vorbemerkung" auf der Titelseite. */
    vorbemerkung: string[];
    /** „Verfasst im Auftrag des … durch …" — klein in der Fusszeile, nie im Kopf. */
    verfasserZeile: string;
    /** Ist das ein Entwurf? Dann sagen es Status, Vorbemerkung und Kopfzeile. */
    entwurf: boolean;
    knoten: Knoten[];
}

/** Alle Ueberschriften der Ebene 1 — die Gliederung, aus der das Verzeichnis entsteht. */
export const gliederung = (dok: Dokument): KnotenUeberschrift[] =>
    dok.knoten.filter((k): k is KnotenUeberschrift => k.art === 'ueberschrift' && k.ebene === 1);

/**
 * Die Einträge des Inhaltsverzeichnisses: jede Überschrift der Ebenen 1 und 2
 * mit ihrer Stelle in `knoten`.
 *
 * Zugeordnet wird über die Stelle, nicht über die Nummer: Rahmenkapitel
 * (Bestätigung der Kenntnisnahme) und die Teile des HSE-Plans haben keine.
 */
export const inhaltsverzeichnis = (dok: Dokument): { index: number; ueberschrift: KnotenUeberschrift }[] =>
    dok.knoten.flatMap((k, index) => (k.art === 'ueberschrift' && k.ebene <= 2 ? [{ index, ueberschrift: k }] : []));
